import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import type { CategoryEntity } from '@actual-app/core/types/models';
import { css } from '@emotion/css';

import { separarEmoji } from '#components/mobile/ui/emoji';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import { Pildora } from '#components/mobile/ui/Pildora';
import { zonaTactilLateral } from '#components/mobile/ui/tactil';
import { movimiento } from '#components/mobile/ui/tokens';
import { useCategories } from '#hooks/useCategories';
import { useSheetValue } from '#hooks/useSheetValue';
import { envelopeBudget } from '#spreadsheet/bindings';

import { CheckPagada, usePagadaFila } from './CheckPagada';
import { marcaManual, muestraCirculoPagada } from './pagadas';
import { useDespertarAuto, useIgnorarMes } from './useIgnorarMes';
import { usePagadas } from './usePagadas';

/** Ancho del botón ✓ (icono de 16 px; la zona táctil se mete en el hueco
 * entre columnas con márgenes negativos). */
const ANCHO = 26;
const ALTO = 44;

/**
 * Zona táctil (B2 · H04, B4 · H31) sin cambiar el aspecto: el alto de la
 * fila (52) y 9 px más a cada lado del ✓. Queda 44 × 52 px.
 */
export const ZONA_FILA = { alto: 52, extra: 9 } as const;
const claseZonaPagada = css(
  zonaTactilLateral({
    izquierda: ZONA_FILA.extra,
    derecha: ZONA_FILA.extra,
    alto: ZONA_FILA.alto,
  }),
);

/**
 * Lo que va en la fila de una categoría (Plan y «Asignar el mes») entre el
 * nombre y la columna de asignado (B4 · H31): la píldora «Pausada» cuando lo
 * está (pausar se hace desde el ⋯ del teclado o la ficha) y el círculo de
 * «Pagado este mes» solo en las filas de facturas. Tocarlo no abre el
 * teclado.
 */
export function BotonesFila({
  category,
  month,
}: {
  category: CategoryEntity;
  month: string;
}) {
  const { data: { grouped = [] } = {} } = useCategories();
  const nombreGrupo = grouped.find(g => g.id === category.group)?.name;
  const { fijos } = usePagadas(month);
  const { pagada } = usePagadaFila(category.id);
  const conCirculo = muestraCirculoPagada({
    nombreGrupo,
    fijo: fijos.has(category.id),
    pagada,
  });
  return (
    <View
      data-testid="botones-fila"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 0,
        // Sin sumar alto a la fila (la línea del nombre mide 20 px).
        // y recupera 4 px a cada lado del hueco entre columnas.
        margin: `-${(ALTO - 20) / 2}px -6px`,
        position: 'relative',
        zIndex: 1,
        pointerEvents: 'auto',
      }}
    >
      <PausaFila category={category} month={month} />
      {conCirculo && (
        <BotonPagada id={category.id} nombre={category.name} month={month} />
      )}
    </View>
  );
}

const estiloBoton = {
  width: ANCHO,
  height: ALTO,
  minHeight: ALTO,
  padding: 0,
  borderRadius: 12,
  backgroundColor: 'transparent',
  position: 'relative',
} as const;

/** ✓: gris sin pagar; verde detectada o marcada. */
function BotonPagada({
  id,
  nombre,
  month,
}: {
  id: string;
  nombre: string;
  month: string;
}) {
  const { t } = useTranslation();
  const { pagada, reciente } = usePagadaFila(id);
  const { manuales, marcar } = usePagadas(month);
  const marca = marcaManual(manuales, id);
  const [menu, setMenu] = useState(false);

  const onPress = () => {
    if (marca === 'pagada') {
      marcar(id, false);
    } else if (pagada) {
      // Detectada sola: se ofrece «Desmarcar este mes».
      setMenu(true);
    } else {
      marcar(id, true);
    }
  };

  return (
    <>
      <Button
        variant="bare"
        aria-pressed={pagada}
        aria-label={
          pagada ? t('Paid this month') : t('Mark as paid this month')
        }
        data-testid="fila-pagada"
        data-pagada={pagada || undefined}
        data-zona-tactil="derecha"
        className={claseZonaPagada}
        onPress={onPress}
        style={({ isPressed }) => ({
          ...estiloBoton,
          overflow: 'visible',
          transform: isPressed ? 'scale(0.9)' : undefined,
          transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
        })}
      >
        <CheckPagada size={16} pagada={pagada} animar={reciente} />
      </Button>
      {menu && (
        <HojaAcciones
          titulo={separarEmoji(nombre).resto}
          emoji={separarEmoji(nombre).emoji}
          subtitulo={t('Paid this month (detected)')}
          onCerrar={() => setMenu(false)}
          data-testid="hoja-pagada"
          filas={[
            {
              id: 'no',
              texto: t('Unmark this month'),
              icono: 'x',
              onPress: () => {
                setMenu(false);
                marcar(id, 'no');
              },
            },
          ]}
        />
      )}
    </>
  );
}

/**
 * Píldora pequeña «Suficiente» (con ✓) si la categoría no pide dinero este mes. También
 * vigila el despertar automático de las dormidas (ver `dormir.ts`), que antes
 * hacía el botón de la fila.
 */
function PausaFila({
  category,
  month,
}: {
  category: CategoryEntity;
  month: string;
}) {
  const { ignorada } = useIgnorarMes(category.id, month);
  const actividad = useSheetValue<'envelope-budget', 'sum-amount'>(
    envelopeBudget.catSumAmount(category.id),
  );
  const saldo = useSheetValue<'envelope-budget', 'leftover'>(
    envelopeBudget.catBalance(category.id),
  );
  const asignado = useSheetValue<'envelope-budget', 'budget'>(
    envelopeBudget.catBudgeted(category.id),
  );
  useDespertarAuto({
    categoryId: category.id,
    month,
    actividad,
    saldo,
    asignado,
  });
  if (!ignorada) {
    return null;
  }
  return (
    <Pildora
      estado="ignorada"
      data-testid="fila-pausada"
      style={{
        minHeight: 18,
        padding: '0 6px',
        fontSize: 11,
        marginRight: 6,
      }}
    >
      <Trans>Enough</Trans>
    </Pildora>
  );
}
